import {
  createLoader,
  createParser,
  createSerializer,
  parseAsBoolean,
  parseAsString,
  parseAsStringLiteral,
} from "nuqs/server"
import { WORKOS_CALLBACK_ERROR_KEYS } from "@/lib/auth/workos/constants"
import { SESSION_EXPIRED_REASON } from "@/lib/constants/auth"
import { isSafeRedirectPath } from "@/lib/utils/isSafeRedirectPath"

export type { LoginErrorCode } from "@/lib/auth/loginErrorCodes"

// Single-slash same-origin paths only — guards against open-redirect via
// `?redirect=https://evil` or `?redirect=//evil`. Returning `null` from
// `parse` lets the `withDefault("/")` below provide the safe fallback.
export const parseAsSafeRedirect = createParser({
  parse: (value) => (isSafeRedirectPath(value) ? value : null),
  serialize: (value) => value,
}).withDefault("/")

export const loginSearchParams = {
  // Set when AuthKit lands here after a sign-in it started itself (a direct
  // visit to the AuthKit domain, a password reset). Never redeemed or logged.
  code: parseAsString,
  redirect: parseAsSafeRedirect,
  error: parseAsString,
  reason: parseAsStringLiteral([SESSION_EXPIRED_REASON]),
}

export const loadLoginSearchParams = createLoader(loginSearchParams)

export const serializeLoginSearchParams = createSerializer(loginSearchParams)

// Same guard as `parseAsSafeRedirect` without the "/" default, so the redeem
// route can tell "no destination asked for" from "asked for the root".
const parseAsSafeRedirectOrNull = createParser({
  parse: (value) => (isSafeRedirectPath(value) ? value : null),
  serialize: (value) => value,
})

export const workosRedeemSearchParams = {
  code: parseAsString,
  redirect: parseAsSafeRedirectOrNull,
}

export const loadWorkosRedeemSearchParams = createLoader(
  workosRedeemSearchParams,
)

export const ssoSearchParams = {
  redirectAfterSiroLogin: parseAsSafeRedirect,
}

export const loadSsoSearchParams = createLoader(ssoSearchParams)

// IdP callback params. Both are opaque strings the backend echoes back into
// the redirect URL; we only forward them to the exchange callable.
export const ssoCallbackSearchParams = {
  code: parseAsString,
  state: parseAsString,
}

export const loadSsoCallbackSearchParams = createLoader(ssoCallbackSearchParams)

export const workosLoginSearchParams = {
  error: parseAsString,
  // Same-origin only: this is stored on the flow's transaction and becomes the
  // post-login destination, so an unsafe value would be an open redirect.
  redirect: parseAsSafeRedirect,
  resume: parseAsBoolean.withDefault(false),
  stepUp: parseAsBoolean.withDefault(false),
}

export const loadWorkosLoginSearchParams = createLoader(workosLoginSearchParams)

export const serializeWorkosLoginSearchParams = createSerializer(
  workosLoginSearchParams,
)

// The root-admin page the proxy turned away; the step-up lands back on it.
export const verifyIdentitySearchParams = {
  redirect: parseAsSafeRedirect,
}

export const loadVerifyIdentitySearchParams = createLoader(
  verifyIdentitySearchParams,
)

// AuthKit callback: authorization `code` (or IdP error) plus the CSRF `state`
// echoed from the entry route's cookie. Never log `code` — it exchanges for a
// WorkOS access token then a Firebase custom token.
export const workosCallbackSearchParams = {
  code: parseAsString,
  error: parseAsString,
  state: parseAsString,
}

export const loadWorkosCallbackSearchParams = createLoader(
  workosCallbackSearchParams,
)

// The callback route handler's failure surface. A literal parser means an
// unknown or hand-edited `reason` falls back to the generic copy instead of
// asking `t()` for a key that doesn't exist.
export const workosErrorSearchParams = {
  reason: parseAsStringLiteral(WORKOS_CALLBACK_ERROR_KEYS).withDefault(
    "generic",
  ),
}

export const loadWorkosErrorSearchParams = createLoader(workosErrorSearchParams)
